import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  Plus, Calendar, Download, AlertTriangle, CheckCircle2, 
  Clock, FileText, Info, Briefcase,
  CalendarDays, AlertCircle, Loader2,
  Palmtree, Stethoscope, Home, Baby, PartyPopper, Video, Sparkles
} from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Textarea } from './ui/textarea';
import { formatDate, formatDateShort, getTodayLocal } from '../lib/dateUtils';
import { HeaderSkeleton, MetricCardsSkeleton, TableSkeleton } from './skeletons/Skeletons';
import JoinedBadge from './leaves/JoinedBadge';

const BACKEND_URL = '';

const employeeApi = axios.create({
  baseURL: BACKEND_URL,
  timeout: 30000,
});

employeeApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('employee_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export default function EmployeeLeavesPage() {
  const [leaves, setLeaves] = useState([]);
  const [balance, setBalance] = useState({});
  const [policies, setPolicies] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showTasksModal, setShowTasksModal] = useState(false);
  const [showOptionalHolidays, setShowOptionalHolidays] = useState(false);
  const [tasksOnDates, setTasksOnDates] = useState([]);
  const [optionalHolidays, setOptionalHolidays] = useState({ available: [], selected: [] });
  const [selectedOptionalHolidays, setSelectedOptionalHolidays] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);

  // Festival suggestion modal state — employees can propose a missing festival
  // day → admin reviews → on approval it joins the org-wide optional list.
  const [showSuggestFestival, setShowSuggestFestival] = useState(false);
  const [festivalSuggestions, setFestivalSuggestions] = useState([]);
  const [festivalForm, setFestivalForm] = useState({ date: '', festival_name: '', reason: '' });
  const [submittingFestival, setSubmittingFestival] = useState(false);
  
  const [formData, setFormData] = useState({ 
    leave_type: '', 
    start_date: '', 
    end_date: '', 
    is_half_day: false,
    half_day_type: 'first_half',
    reason: '',
    medical_certificate_url: '',
    acknowledged_tasks: false,
    notes: '',
    compensating_for_date: '',  // required when leave_type === 'comp'
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [leavesRes, balanceRes, policiesRes, metricsRes] = await Promise.all([
        employeeApi.get('/api/employee/leaves'),
        employeeApi.get('/api/employee/leaves/balance'),
        employeeApi.get('/api/leave-policies'),
        employeeApi.get('/api/employee/leaves/metrics')
      ]);
      setLeaves(leavesRes.data);
      setBalance(balanceRes.data);
      setPolicies(policiesRes.data);
      setMetrics(metricsRes.data);
      
      // Set default leave type, excluding mandatory (ml) and optional (ol) holidays
      const applicableLeaveTypes = policiesRes.data.filter(p => p.code !== 'ml' && p.code !== 'ol');
      if (applicableLeaveTypes.length > 0) {
        setFormData(prev => ({ ...prev, leave_type: applicableLeaveTypes[0].code }));
      }
    } catch (error) {
      toast.error('Failed to load leave data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchOptionalHolidays = async () => {
    try {
      const res = await employeeApi.get('/api/employee/leaves/optional-holidays');
      setOptionalHolidays(res.data);
      setSelectedOptionalHolidays(res.data.selected || []);
    } catch (error) {
      toast.error('Failed to load optional holidays');
    }
  };

  const handleDateChange = async (field, value) => {
    const newFormData = { ...formData, [field]: value };
    
    // Auto-uncheck half day if date range spans multiple days
    if (newFormData.start_date && newFormData.end_date && newFormData.start_date !== newFormData.end_date) {
      newFormData.is_half_day = false;
    }
    
    setFormData(newFormData);
    
    if (newFormData.start_date && newFormData.end_date && newFormData.leave_type) {
      try {
        const tasksRes = await employeeApi.get(`/api/employee/leaves/tasks-on-dates?start_date=${newFormData.start_date}&end_date=${newFormData.end_date}`);
        setTasksOnDates(tasksRes.data);
      } catch (error) {
        console.error('Failed to fetch tasks');
      }
      
      await validateLeave(newFormData);
    }
  };

  const validateLeave = async (data = formData) => {
    if (!data.start_date || !data.end_date || !data.leave_type) return;
    
    setValidating(true);
    try {
      const res = await employeeApi.post('/api/employee/leaves/validate', data);
      setValidation(res.data);
    } catch (error) {
      setValidation({ valid: false, errors: [error.response?.data?.detail || 'Validation failed'] });
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (tasksOnDates.length > 0 && !formData.acknowledged_tasks) {
      setShowTasksModal(true);
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await employeeApi.post('/api/employee/leaves', formData);
      toast.success(res.data.message);
      setShowForm(false);
      // Reset form with first applicable leave type (excluding ml and ol)
      const applicableLeaveTypes = policies.filter(p => p.code !== 'ml' && p.code !== 'ol');
      setFormData({ 
        leave_type: applicableLeaveTypes[0]?.code || '', 
        start_date: '', 
        end_date: '', 
        is_half_day: false,
        half_day_type: 'first_half',
        reason: '',
        medical_certificate_url: '',
        acknowledged_tasks: false,
        notes: ''
      });
      setValidation(null);
      setTasksOnDates([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit leave request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTaskAcknowledge = () => {
    setFormData({ ...formData, acknowledged_tasks: true });
    setShowTasksModal(false);
  };

  const downloadTasksCSV = () => {
    const headers = ['Task', 'Client', 'Due Date', 'Status', 'Priority'];
    const rows = tasksOnDates.map(t => [
      t.title || t.name || '',
      t.client_name || '',
      t.due_date || '',
      t.status || '',
      t.priority || ''
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tasks_during_leave_${formData.start_date}_${formData.end_date}.csv`;
    a.click();
  };

  const handleDeleteLeave = async (leaveId) => {
    if (!window.confirm('Are you sure you want to withdraw this leave request?')) return;
    
    try {
      await employeeApi.delete(`/api/employee/leaves/${leaveId}`);
      toast.success('Leave request withdrawn');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to withdraw');
    }
  };

  const handleCancelApprovedLeave = async (leaveId) => {
    if (!window.confirm('Are you sure you want to cancel this approved leave? Your balance will be restored.')) return;
    
    try {
      const res = await employeeApi.post(`/api/employee/leaves/${leaveId}/cancel`);
      toast.success(`Leave cancelled. ${res.data.days_restored} day(s) restored to your balance.`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel leave');
    }
  };

  // Check if a leave can be cancelled (approved and not yet started)
  const canCancelApprovedLeave = (leave) => {
    if (leave.status !== 'approved') return false;
    const startDate = new Date(leave.start_date + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return startDate > today;
  };

  // Festival suggestion handlers — propose a missing festival day for the
  // org-wide Optional Holiday list. Admin reviews; on approval it joins the menu.
  const fetchFestivalSuggestions = async () => {
    try {
      const r = await employeeApi.get('/api/employee/festival-suggestions');
      setFestivalSuggestions(Array.isArray(r.data) ? r.data : []);
    } catch {
      // silent — panel is opt-in
    }
  };
  const submitFestivalSuggestion = async () => {
    if (!festivalForm.date || !festivalForm.festival_name.trim()) {
      toast.error('Please provide both a date and a festival name.');
      return;
    }
    setSubmittingFestival(true);
    try {
      await employeeApi.post('/api/employee/festival-suggestions', {
        date: festivalForm.date,
        festival_name: festivalForm.festival_name.trim(),
        reason: festivalForm.reason.trim(),
      });
      toast.success('Festival suggestion submitted for admin review.');
      setFestivalForm({ date: '', festival_name: '', reason: '' });
      fetchFestivalSuggestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit suggestion');
    } finally {
      setSubmittingFestival(false);
    }
  };

  const saveOptionalHolidays = async () => {
    // Allow saving any selection from 1-5 (as per the 12-day deadline rule)
    if (selectedOptionalHolidays.length === 0) {
      toast.error('Please select at least one optional holiday');
      return;
    }
    if (selectedOptionalHolidays.length > 5) {
      toast.error('You can select maximum 5 optional holidays');
      return;
    }
    
    try {
      await employeeApi.post('/api/employee/leaves/optional-holidays', {
        selected_holidays: selectedOptionalHolidays
      });
      toast.success('Optional holidays saved successfully');
      setShowOptionalHolidays(false);
      fetchOptionalHolidays(); // Refresh to show updated data
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.errors) {
        toast.error(detail.errors.join(', '));
      } else {
        toast.error(detail || 'Failed to save optional holidays');
      }
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-50 text-red-700 border border-red-200 font-medium">Rejected</Badge>;
      case 'pending':
        return <Badge className="bg-amber-50 text-amber-700 border border-amber-200 font-medium">Pending</Badge>;
      case 'cancelled':
        return <Badge className="bg-white/40 text-neutral-700 border border-white/40 font-medium">Cancelled</Badge>;
      default:
        return <Badge className="bg-white/40 text-neutral-700 border border-white/40">{status}</Badge>;
    }
  };

  const getLeaveTypeIcon = (code) => {
    const icons = {
      pl: Palmtree,        // Privilege / personal
      sl: Stethoscope,     // Sick
      wfh: Home,           // Work from home
      ml: Baby,            // Maternity / parental
      ol: PartyPopper,     // Optional / occasion
      prod: Video,         // Production
    };
    return icons[code] || Calendar;
  };

  // Determine if half-day should be disabled (only available for single day)
  const isHalfDayDisabled = Boolean(
    formData.start_date && 
    formData.end_date && 
    formData.start_date !== formData.end_date
  );

  if (loading) {
    return (
      <div className="p-4 md:p-8 space-y-6" data-testid="employee-leaves-loading">
        <HeaderSkeleton />
        <MetricCardsSkeleton count={4} />
        <TableSkeleton rows={5} columns={5} />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8" data-testid="leave-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 md:mb-8">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-[#1a1a2e]">Leave Management</h1>
          <p className="text-sm md:text-base text-neutral-500 mt-1">Apply for leaves, view balances and track requests</p>
        </div>
        <div className="flex gap-2 md:gap-3">
          <Button 
            variant="outline" 
            onClick={() => { setShowSuggestFestival(true); fetchFestivalSuggestions(); }}
            className="border-white/40 text-neutral-700 hover:bg-white/60 text-sm md:text-base"
            data-testid="suggest-festival-btn"
          >
            <Sparkles className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Suggest a Festival</span>
          </Button>
          <Button 
            variant="outline" 
            onClick={() => { fetchOptionalHolidays(); setShowOptionalHolidays(true); }}
            className="border-white/40 text-neutral-700 hover:bg-white/60 text-sm md:text-base"
            data-testid="select-optional-holidays-btn"
          >
            <CalendarDays className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Optional Holidays</span>
          </Button>
          <Button 
            onClick={() => setShowForm(true)}
            className="bg-pink-600 hover:bg-pink-700 text-white shadow-soft text-sm md:text-base"
            data-testid="apply-leave-btn"
          >
            <Plus className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Apply for Leave</span>
            <span className="md:hidden">Apply</span>
          </Button>
        </div>
      </div>

      {/* Metrics Cards */}
      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5 mb-6 md:mb-8">
          <div className="glass-card p-3 md:p-5 shadow-soft hover:shadow-soft transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-neutral-500 font-medium">Total Requests</p>
                <p className="text-xl md:text-2xl font-bold text-[#1a1a2e] mt-1">{metrics.total_requests}</p>
              </div>
              <div className="w-9 h-9 md:w-11 md:h-11 bg-blue-50 rounded-xl flex items-center justify-center">
                <FileText className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
              </div>
            </div>
          </div>
          <div className="glass-card p-3 md:p-5 shadow-soft hover:shadow-soft transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-neutral-500 font-medium">Days Taken</p>
                <p className="text-xl md:text-2xl font-bold text-[#1a1a2e] mt-1">{metrics.total_days_taken}</p>
              </div>
              <div className="w-9 h-9 md:w-11 md:h-11 bg-emerald-50 rounded-xl flex items-center justify-center">
                <Calendar className="w-4 h-4 md:w-5 md:h-5 text-emerald-600" />
              </div>
            </div>
          </div>
          <div className="glass-card p-3 md:p-5 shadow-soft hover:shadow-soft transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-neutral-500 font-medium">Pending</p>
                <p className="text-xl md:text-3xl font-bold text-amber-600 mt-1">{metrics.pending}</p>
              </div>
              <div className="w-9 h-9 md:w-11 md:h-11 bg-amber-50 rounded-xl flex items-center justify-center">
                <Clock className="w-4 h-4 md:w-5 md:h-5 text-amber-600" />
              </div>
            </div>
          </div>
          <div className="glass-card p-3 md:p-5 shadow-soft hover:shadow-soft transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-neutral-500 font-medium">Approved</p>
                <p className="text-xl md:text-3xl font-bold text-emerald-600 mt-1">{metrics.approved}</p>
              </div>
              <div className="w-9 h-9 md:w-11 md:h-11 bg-emerald-50 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-emerald-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Leave Balance Cards */}
      <h2 className="text-base md:text-lg font-semibold text-[#1a1a2e] mb-3 md:mb-4">Leave Balances</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 md:gap-4 mb-6 md:mb-8">
        {policies.map(policy => {
          const bal = balance[policy.code] || {};
          const percentage = bal.available && bal.pro_rata_allocation 
            ? Math.round((bal.available / bal.pro_rata_allocation) * 100) 
            : 100;
          return (
            <div 
              key={policy._id} 
              className="glass-card p-3 md:p-5 shadow-soft hover:shadow-soft transition-all hover:border-pink-200 cursor-default"
              data-testid={`balance-card-${policy.code}`}
            >
              <div className="text-xl md:text-2xl mb-1 md:mb-2 flex justify-center">{(() => { const Icon = getLeaveTypeIcon(policy.code); return <Icon className="w-6 h-6 text-charcoal-700" />; })()}</div>
              <p className="text-[10px] md:text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                {policy.code}
              </p>
              <p className="text-xl md:text-2xl font-bold text-[#1a1a2e] mt-0.5 md:mt-1">{bal.available || 0}</p>
              <div className="mt-1.5 md:mt-2 h-1 md:h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-pink-500 rounded-full transition-all"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <p className="text-[10px] md:text-xs text-neutral-400 mt-1 md:mt-2">
                of {bal.pro_rata_allocation || policy.annual_allocation}
              </p>
              {bal.pending > 0 && (
                <p className="text-[10px] md:text-xs text-amber-600 mt-0.5 md:mt-1 font-medium">{bal.pending} pending</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Policy Info */}
      <div className="bg-white/40 rounded-xl border border-white/40 p-4 md:p-6 mb-6 md:mb-8">
        <h3 className="font-semibold text-[#1a1a2e] mb-3 md:mb-4 flex items-center gap-2 text-sm md:text-base">
          <Info className="w-4 h-4 text-pink-600" />
          Leave Policy Highlights
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 text-xs md:text-sm">
          <div>
            <p className="font-semibold text-[#1a1a2e] mb-2">Privilege Leave (PL)</p>
            <ul className="text-neutral-600 space-y-1 md:space-y-1.5">
              <li className="flex items-start gap-2"><span className="text-pink-500">•</span> 2 days advance notice</li>
              <li className="flex items-start gap-2"><span className="text-pink-500">•</span> Sandwich rule applies</li>
              <li className="flex items-start gap-2"><span className="text-pink-500">•</span> Cannot club with SL</li>
              <li className="flex items-start gap-2"><span className="text-pink-500">•</span> Max 45 days carry forward</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-[#1a1a2e] mb-2">Sick Leave (SL)</p>
            <ul className="text-neutral-600 space-y-1 md:space-y-1.5">
              <li className="flex items-start gap-2"><span className="text-emerald-500">•</span> Auto-approved</li>
              <li className="flex items-start gap-2"><span className="text-emerald-500">•</span> Medical cert for 2+ days</li>
              <li className="flex items-start gap-2"><span className="text-emerald-500">•</span> No carry forward</li>
              <li className="flex items-start gap-2"><span className="text-emerald-500">•</span> Cannot club with PL</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-[#1a1a2e] mb-2">Work From Home (WFH)</p>
            <ul className="text-neutral-600 space-y-1 md:space-y-1.5">
              <li className="flex items-start gap-2"><span className="text-blue-500">•</span> Manager approval needed</li>
              <li className="flex items-start gap-2"><span className="text-blue-500">•</span> No sandwich rule</li>
              <li className="flex items-start gap-2"><span className="text-blue-500">•</span> Can club with any leave</li>
              <li className="flex items-start gap-2"><span className="text-blue-500">•</span> No carry forward</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Leave History Table */}
      <h2 className="text-base md:text-lg font-semibold text-[#1a1a2e] mb-3 md:mb-4">Leave History</h2>
      
      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {leaves.length === 0 ? (
          <div className="glass-card p-8 text-center">
            <Calendar className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
            <p className="text-neutral-500">No leave requests yet</p>
          </div>
        ) : (
          leaves.map((leave) => (
            <div key={leave._id} className="glass-card p-4 shadow-soft">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{(() => { const Icon = getLeaveTypeIcon(leave.leave_type); return <Icon className="w-5 h-5 text-charcoal-700" />; })()}</span>
                  <div>
                    <p className="font-medium text-[#1a1a2e]">{leave.leave_type_name || leave.leave_type?.toUpperCase()}</p>
                    <p className="text-xs text-neutral-500">{leave.days_count} day(s) {leave.is_half_day && `(${leave.half_day_type === 'first_half' ? '1st half' : '2nd half'})`}</p>
                  </div>
                </div>
                {getStatusBadge(leave.status)}
              </div>
              <div className="text-sm text-neutral-600 mb-2">
                <span className="font-medium">Dates: </span>
                {formatDateShort(leave.start_date)}
                {leave.start_date !== leave.end_date && ` - ${formatDateShort(leave.end_date)}`}
              </div>
              {leave.reason && (
                <p className="text-xs text-neutral-500 mb-3 line-clamp-2">{leave.reason}</p>
              )}
              <div className="flex items-center gap-2">
                {leave.status === 'pending' && (
                  <button
                    onClick={() => handleDeleteLeave(leave._id)}
                    className="text-red-600 hover:text-red-700 text-xs font-medium"
                  >
                    Withdraw
                  </button>
                )}
                {canCancelApprovedLeave(leave) && (
                  <button
                    onClick={() => handleCancelApprovedLeave(leave._id)}
                    className="text-amber-600 hover:text-amber-700 text-xs font-medium"
                  >
                    Cancel
                  </button>
                )}
                {leave.sandwich_applied && (
                  <span className="text-[10px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">sandwich</span>
                )}
                <JoinedBadge leave={leave} />
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block glass-card shadow-soft overflow-hidden">
        <table className="min-w-full" data-testid="leave-history-table">
          <thead className="bg-white/40 border-b border-white/40">
            <tr>
              <th className="px-6 py-3.5 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wide">Type</th>
              <th className="px-6 py-3.5 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wide">Dates</th>
              <th className="px-6 py-3.5 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wide">Days</th>
              <th className="px-6 py-3.5 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wide">Reason</th>
              <th className="px-6 py-3.5 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wide">Status</th>
              <th className="px-6 py-3.5 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/30">
            {leaves.map((leave) => (
              <tr key={leave._id} className="hover:bg-white/60 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{(() => { const Icon = getLeaveTypeIcon(leave.leave_type); return <Icon className="w-5 h-5 text-charcoal-700" />; })()}</span>
                    <span className="font-medium text-[#1a1a2e]">{leave.leave_type_name || leave.leave_type?.toUpperCase()}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-neutral-600">
                  <div className="flex flex-col">
                    <span>{formatDate(leave.start_date)}</span>
                    {leave.start_date !== leave.end_date && (
                      <span className="text-neutral-400">to {formatDate(leave.end_date)}</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm font-semibold text-[#1a1a2e]">{leave.days_count}</span>
                  {leave.is_half_day && <span className="text-xs text-neutral-500 ml-1">({leave.half_day_type === 'first_half' ? '1st half' : '2nd half'})</span>}
                  {leave.sandwich_applied && (
                    <span className="ml-2 text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">sandwich</span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-neutral-600 max-w-xs truncate" title={leave.reason}>
                  {leave.reason}
                </td>
                <td className="px-6 py-4">{getStatusBadge(leave.status)}</td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    {leave.status === 'pending' && (
                      <button
                        onClick={() => handleDeleteLeave(leave._id)}
                        className="text-red-600 hover:text-red-700 text-sm font-medium hover:underline"
                        data-testid={`withdraw-leave-${leave._id}`}
                      >
                        Withdraw
                      </button>
                    )}
                    {canCancelApprovedLeave(leave) && (
                      <button
                        onClick={() => handleCancelApprovedLeave(leave._id)}
                        className="text-amber-600 hover:text-amber-700 text-sm font-medium hover:underline"
                        data-testid={`cancel-leave-${leave._id}`}
                      >
                        Cancel
                      </button>
                    )}
                    {leave.admin_note && (
                      <span className="text-neutral-400 cursor-help" title={`Admin note: ${leave.admin_note}`}>
                        <Info className="w-4 h-4 inline" />
                      </span>
                    )}
                    {leave.status === 'cancelled' && (
                      <span className="text-xs text-neutral-400">
                        {leave.cancelled_by_type === 'hr' ? 'By HR'
                          : leave.cancelled_by_type === 'admin' ? 'By Admin'
                          : leave.cancelled_by_type === 'employee' ? 'By You'
                          : 'By You'}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {leaves.length === 0 && (
          <div className="text-center py-16 text-neutral-400">
            <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-200" />
            <p className="font-medium">No leave requests yet</p>
            <p className="text-sm mt-1">Click &quot;Apply for Leave&quot; to get started</p>
          </div>
        )}
      </div>

      {/* Apply Leave Modal */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white mx-4 md:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-lg md:text-xl font-bold text-[#1a1a2e]">Apply for Leave</DialogTitle>
            <DialogDescription className="text-sm md:text-base text-neutral-500">
              Fill in the details to submit your leave application
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-5 py-4">
            {/* Leave Type - Excludes Mandatory (ml) and Optional (ol) holidays as they have separate flows */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">Leave Type *</label>
              <Select 
                value={formData.leave_type} 
                onValueChange={(val) => { setFormData({ ...formData, leave_type: val }); validateLeave({ ...formData, leave_type: val }); }}
              >
                <SelectTrigger data-testid="leave-type-select" className="bg-white">
                  <SelectValue placeholder="Select leave type" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {policies
                    .filter(p => p.code !== 'ml' && p.code !== 'ol')
                    .map(p => (
                    <SelectItem key={p._id} value={p.code}>
                      <span className="flex items-center gap-2">
                        <span>{(() => { const Icon = getLeaveTypeIcon(p.code); return <Icon className="w-4 h-4 text-charcoal-600" />; })()}</span>
                        {p.name}
                        {p.is_unlimited
                          ? <span className="text-xs text-purple-600 ml-1">(no cap — needs approval)</span>
                          : <span className="text-xs text-neutral-500 ml-1">({balance[p.code]?.available || 0} available)</span>
                        }
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">Start Date *</label>
                <input
                  type="date"
                  required
                  value={formData.start_date}
                  onChange={(e) => handleDateChange('start_date', e.target.value)}
                  className="w-full px-4 py-2.5 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50 bg-white text-[#1a1a2e] transition-colors"
                  data-testid="start-date-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">End Date *</label>
                <input
                  type="date"
                  required
                  value={formData.end_date}
                  onChange={(e) => handleDateChange('end_date', e.target.value)}
                  className="w-full px-4 py-2.5 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50 bg-white text-[#1a1a2e] transition-colors"
                  data-testid="end-date-input"
                />
              </div>
            </div>

            {/* Half Day Option */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-4">
                <label className={`flex items-center gap-2 ${isHalfDayDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    checked={formData.is_half_day && !isHalfDayDisabled}
                    onChange={(e) => setFormData({ ...formData, is_half_day: e.target.checked })}
                    disabled={isHalfDayDisabled}
                    className="w-4 h-4 rounded border-white/40 text-pink-600 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="half-day-checkbox"
                  />
                  <span className="text-sm text-neutral-700">Half Day</span>
                </label>
                {formData.is_half_day && !isHalfDayDisabled && (
                  <Select value={formData.half_day_type} onValueChange={(val) => setFormData({ ...formData, half_day_type: val })}>
                    <SelectTrigger className="w-40 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="first_half">First Half (Morning off)</SelectItem>
                      <SelectItem value="second_half">Second Half (Afternoon off)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              {isHalfDayDisabled && (
                <p className="text-xs text-neutral-500 ml-6">Half day is only available for single-day leave</p>
              )}
            </div>

            {/* Validation Results */}
            {validating && (
              <div className="flex items-center gap-2 text-neutral-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Validating...</span>
              </div>
            )}
            
            {validation && (
              <div className="space-y-2">
                {validation.errors?.map((err, i) => (
                  <div key={i} className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-xl">
                    <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{err}</p>
                  </div>
                ))}
                {validation.warnings?.map((warn, i) => (
                  <div key={i} className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-100 rounded-xl">
                    <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-700">{warn}</p>
                  </div>
                ))}
                {validation.valid && validation.calculated_days && (
                  <div className="flex items-start gap-2 p-3 bg-emerald-50 border border-emerald-100 rounded-xl">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-emerald-700">
                      {validation.calculated_days} day(s) will be deducted
                      {validation.auto_approved && <span className="ml-1 font-medium">(Auto-approved)</span>}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Tasks During Leave */}
            {tasksOnDates.length > 0 && (
              <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-amber-800 flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    {tasksOnDates.length} task(s) due during your leave
                  </h4>
                  <button
                    type="button"
                    onClick={downloadTasksCSV}
                    className="text-sm text-amber-700 hover:text-amber-800 flex items-center gap-1 hover:underline"
                  >
                    <Download className="w-4 h-4" />
                    Download CSV
                  </button>
                </div>
                <div className="max-h-28 overflow-y-auto">
                  <ul className="text-sm text-amber-700 space-y-1">
                    {tasksOnDates.slice(0, 5).map(task => (
                      <li key={task._id} className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                        {task.title || task.name} - Due: {task.due_date}
                      </li>
                    ))}
                    {tasksOnDates.length > 5 && (
                      <li className="text-amber-600 italic">...and {tasksOnDates.length - 5} more</li>
                    )}
                  </ul>
                </div>
              </div>
            )}

            {/* Reason */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">Reason *</label>
              <Textarea
                required
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                rows={3}
                placeholder="Please provide the reason for your leave"
                className="bg-white border-white/40 focus:ring-pink-500 focus:border-pink-500"
                data-testid="leave-reason-input"
              />
            </div>

            {/* Medical Certificate (for SL) */}
            {formData.leave_type === 'sl' && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Medical Certificate URL (required for 2+ days)
                </label>
                <input
                  type="url"
                  value={formData.medical_certificate_url}
                  onChange={(e) => setFormData({ ...formData, medical_certificate_url: e.target.value })}
                  className="w-full px-4 py-2.5 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50 bg-white text-[#1a1a2e]"
                  placeholder="https://..."
                />
              </div>
            )}

            {/* Compensatory Leave (comp) — REQUIRES the date being compensated for */}
            {formData.leave_type === 'comp' && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3" data-testid="comp-leave-extra">
                <div className="flex items-start gap-2">
                  <span className="text-purple-700 font-medium text-sm">Compensatory Leave</span>
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-purple-100 text-purple-800 rounded">
                    no cap · approval required
                  </span>
                </div>
                <p className="text-xs text-purple-900">
                  You can apply for any number of comp days, but HR must approve
                  each request. You <strong>must</strong> specify the weekend or
                  holiday you actually worked on — your manager will verify
                  against attendance records before approving.
                </p>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Date worked (compensating for) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.compensating_for_date}
                    onChange={(e) => setFormData({ ...formData, compensating_for_date: e.target.value })}
                    max={getTodayLocal()}
                    className="w-full px-4 py-2.5 border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 bg-white text-[#1a1a2e] transition-colors"
                    data-testid="comp-compensating-for-date"
                  />
                  <p className="text-[11px] text-neutral-500 mt-1">
                    Must be a past date (a weekend or holiday you worked on).
                  </p>
                </div>
              </div>
            )}

            {/* Additional Notes */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">Additional Notes</label>
              <input
                type="text"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-4 py-2.5 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50 bg-white text-[#1a1a2e]"
                placeholder="Any additional information"
              />
            </div>

            <DialogFooter className="pt-4 border-t border-white/30">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="border-white/40">
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={
                  submitting
                  || !validation?.valid
                  || (formData.leave_type === 'comp' && !formData.compensating_for_date)
                }
                className="bg-pink-600 hover:bg-pink-700 text-white"
                data-testid="submit-leave-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Submit Application
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tasks Acknowledgment Modal */}
      <Dialog open={showTasksModal} onOpenChange={setShowTasksModal}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              Tasks Due During Leave Period
            </DialogTitle>
            <DialogDescription className="text-neutral-500">
              Review the tasks assigned to you that are due during your leave period.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="flex justify-end mb-3">
              <button
                onClick={downloadTasksCSV}
                className="text-sm text-pink-600 hover:text-pink-700 flex items-center gap-1 hover:underline"
              >
                <Download className="w-4 h-4" />
                Download as CSV
              </button>
            </div>
            
            <div className="max-h-64 overflow-y-auto border border-white/40 rounded-xl">
              <table className="min-w-full">
                <thead className="bg-white/40 sticky top-0">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-neutral-600 uppercase">Task</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-neutral-600 uppercase">Client</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-neutral-600 uppercase">Due Date</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-neutral-600 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/30">
                  {tasksOnDates.map(task => (
                    <tr key={task._id} className="hover:bg-white/60">
                      <td className="px-4 py-3 text-sm text-[#1a1a2e]">{task.title || task.name}</td>
                      <td className="px-4 py-3 text-sm text-neutral-600">{task.client_name || '-'}</td>
                      <td className="px-4 py-3 text-sm text-neutral-600">{task.due_date}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-xs border-white/40">{task.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 p-4 bg-amber-50 border border-amber-100 rounded-xl">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.acknowledged_tasks}
                  onChange={(e) => setFormData({ ...formData, acknowledged_tasks: e.target.checked })}
                  className="mt-0.5 w-4 h-4 rounded border-white/40 text-pink-600 focus:ring-pink-500"
                />
                <span className="text-sm text-amber-800">
                  I acknowledge that I have pending tasks during my leave period and will ensure they are completed 
                  before my leave or properly handed over to a colleague.
                </span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTasksModal(false)} className="border-white/40">
              Go Back
            </Button>
            <Button 
              onClick={handleTaskAcknowledge}
              disabled={!formData.acknowledged_tasks}
              className="bg-pink-600 hover:bg-pink-700 text-white"
            >
              Acknowledge & Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Optional Holidays Modal - 2026 Karnataka State */}
      <Dialog open={showOptionalHolidays} onOpenChange={setShowOptionalHolidays}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1a1a2e]">Select Optional Holidays - {optionalHolidays.current_year || 2026}</DialogTitle>
            <DialogDescription className="text-neutral-500">
              Choose up to 5 optional holidays from the Karnataka State approved list. Selection must be made 12 days before each holiday.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-600">
                  Selected: <span className={`font-bold ${selectedOptionalHolidays.length === 5 ? 'text-emerald-600' : selectedOptionalHolidays.length > 0 ? 'text-amber-600' : 'text-neutral-400'}`}>
                    {selectedOptionalHolidays.length}/{optionalHolidays.max_selection || 5}
                  </span>
                </p>
                {optionalHolidays.selections_remaining !== undefined && (
                  <p className="text-xs text-neutral-400 mt-0.5">
                    {optionalHolidays.selections_remaining} selection(s) remaining
                  </p>
                )}
              </div>
              <Badge variant="outline" className="border-white/40 text-neutral-600">
                Karnataka State {optionalHolidays.current_year || 2026}
              </Badge>
            </div>

            <div className="max-h-[400px] overflow-y-auto border border-white/40 rounded-xl">
              <table className="min-w-full">
                <thead className="bg-white/40 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-2.5 w-10"></th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-neutral-600 uppercase">Date</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-neutral-600 uppercase">Day</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-neutral-600 uppercase">Holiday</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-neutral-600 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {optionalHolidays.available?.map(holiday => {
                    const isSelected = selectedOptionalHolidays.includes(holiday.date) || holiday.is_selected;
                    const isPast = holiday.is_past;
                    const canSelect = holiday.can_select && !isPast;
                    const isDisabled = isPast || !canSelect;
                    
                    return (
                      <tr 
                        key={holiday.date} 
                        className={`transition-colors ${
                          isPast ? 'bg-white/40 opacity-50' : 
                          isSelected ? 'bg-teal-50' : 
                          canSelect ? 'hover:bg-white/60 cursor-pointer' : 'bg-amber-50/30'
                        }`}
                        onClick={() => {
                          // BUG 4 FIX: allow deselecting ANY future selected holiday
                          // (previous logic blocked server-saved ones, breaking REPLACE semantics).
                          // Past-holiday deselect is still blocked by the backend cut-off.
                          if (isSelected) {
                            setSelectedOptionalHolidays(selectedOptionalHolidays.filter(d => d !== holiday.date));
                          } else if (selectedOptionalHolidays.length < 5 && canSelect) {
                            setSelectedOptionalHolidays([...selectedOptionalHolidays, holiday.date]);
                          }
                        }}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isPast || (!isSelected && !canSelect)}
                            onChange={() => {}}
                            className="w-4 h-4 rounded border-white/40 text-teal-600 focus:ring-teal-500"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm font-mono text-[#1a1a2e]">{holiday.date}</td>
                        <td className="px-4 py-3 text-sm text-neutral-600">{holiday.day}</td>
                        <td className="px-4 py-3 text-sm font-medium text-[#1a1a2e]">{holiday.name}</td>
                        <td className="px-4 py-3">
                          {isPast ? (
                            <Badge variant="outline" className="text-xs text-neutral-400 border-white/40">Past</Badge>
                          ) : holiday.is_selected ? (
                            <Badge className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200">Selected</Badge>
                          ) : canSelect ? (
                            <span className="text-xs text-neutral-500">{holiday.days_until_deadline}d left</span>
                          ) : (
                            <Badge variant="outline" className="text-xs text-amber-600 border-amber-200">Deadline passed</Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            <p className="text-xs text-neutral-400 mt-3 flex items-center gap-1">
              <Info className="w-3 h-3" />
              Selections must be made 12 days before each holiday date
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOptionalHolidays(false)} className="border-white/40">
              Cancel
            </Button>
            <Button 
              onClick={saveOptionalHolidays}
              disabled={selectedOptionalHolidays.length === 0}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              Save Selection ({selectedOptionalHolidays.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Festival Suggestion Modal ===== */}
      <Dialog open={showSuggestFestival} onOpenChange={setShowSuggestFestival}>
        <DialogContent className="max-w-xl bg-white" data-testid="festival-suggestion-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1a1a2e]">Suggest a Festival</DialogTitle>
            <DialogDescription className="text-neutral-600">
              Propose a missing festival day. Admin reviews it before adding to the org-wide Optional Holiday list.
              Your selection cap (5 days) does not change — this just expands the menu for everyone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div>
              <label className="block text-xs font-semibold text-neutral-700 mb-1">Date *</label>
              <input
                type="date"
                value={festivalForm.date}
                onChange={(e) => setFestivalForm({ ...festivalForm, date: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-200"
                data-testid="festival-date-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-neutral-700 mb-1">Festival Name *</label>
              <input
                type="text"
                placeholder="e.g. Karwa Chauth"
                value={festivalForm.festival_name}
                onChange={(e) => setFestivalForm({ ...festivalForm, festival_name: e.target.value })}
                maxLength={120}
                className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-200"
                data-testid="festival-name-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-neutral-700 mb-1">Reason / Context (optional)</label>
              <textarea
                rows={3}
                placeholder="Short note for admin"
                value={festivalForm.reason}
                onChange={(e) => setFestivalForm({ ...festivalForm, reason: e.target.value })}
                maxLength={500}
                className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-200"
                data-testid="festival-reason-input"
              />
            </div>

            {festivalSuggestions.length > 0 && (
              <div className="pt-3 border-t border-neutral-100" data-testid="festival-prev-suggestions">
                <h4 className="text-sm font-semibold text-neutral-700 mb-2">Your previous suggestions</h4>
                <ul className="space-y-1 max-h-40 overflow-y-auto">
                  {festivalSuggestions.map((s) => (
                    <li key={s._id} className="flex items-center justify-between text-xs">
                      <span className="text-neutral-800">
                        {s.date} — <span className="font-medium">{s.festival_name}</span>
                      </span>
                      <Badge className={
                        s.status === 'approved' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                        s.status === 'rejected' ? 'bg-red-50 text-red-700 border border-red-200' :
                        'bg-amber-50 text-amber-700 border border-amber-200'
                      }>{s.status}</Badge>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSuggestFestival(false)} className="border-neutral-200">
              Close
            </Button>
            <Button
              onClick={submitFestivalSuggestion}
              disabled={submittingFestival || !festivalForm.date || !festivalForm.festival_name.trim()}
              className="bg-pink-600 hover:bg-pink-700 text-white"
              data-testid="submit-festival-btn"
            >
              {submittingFestival ? 'Submitting…' : 'Submit for Admin Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
